// Select (drawing v2.6, screen 2, row Select): as Input, with a chevron. The
// native select does the opening, so it works with a keyboard and a screen
// reader; over 10 choices the standard asks for a searchable menu, which a
// page builds from Menu when it needs one.

import { forwardRef, useId, type ReactNode, type SelectHTMLAttributes } from "react";
import { ChevronDown } from "lucide-react";
import { Field } from "./Input";
import { cx } from "./cx";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "className"> {
  label: ReactNode;
  optional?: boolean | undefined;
  hint?: ReactNode | undefined;
  error?: ReactNode | undefined;
  options: SelectOption[];
  placeholder?: string | undefined;
  className?: string | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select({ label, optional, hint, error, options, placeholder, className, id, disabled, ...rest }, ref) {
  const auto = useId();
  const selectId = id ?? auto;
  return (
    <Field label={label} optional={optional} hint={hint} error={error} htmlFor={selectId} className={className}>
      <div className={cx("pg-inp", Boolean(error) && "is-error", disabled && "is-disabled")}>
        <select ref={ref} id={selectId} disabled={disabled} aria-invalid={error ? true : undefined} {...rest}>
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown className="pg-ico" aria-hidden />
      </div>
    </Field>
  );
});
